import React, { useState, useEffect, useRef } from 'react';
import PortfolioAdd from './PortfolioAdd';
import AssetDetail from './AssetDetail';
import { useSignalAlerts } from '../hooks/useSignalAlerts';
import { useFreemium } from '../hooks/useFreemium';
import { setItem } from '../services/iCloudStorage';
import { fmtPrice, fmt, logoTicker } from '../utils/format';
import './PortfolioView.css';

function TickerLogo({ ticker }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <div className="pv-logo pv-logo--fallback">{ticker.slice(0, 3)}</div>;
  return (
    <div className="pv-logo">
      <img
        src={`https://assets.parqet.com/logos/symbol/${logoTicker(ticker)}`}
        alt={ticker}
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function SwipeToDelete({ children, onDelete, ticker }) {
  const wrapRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const currentX = useRef(0);
  const swiping = useRef(false);
  const decided = useRef(false);

  const onTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    currentX.current = 0;
    swiping.current = false;
    decided.current = false;
    const el = wrapRef.current?.querySelector('.pv-list-item');
    if (el) el.style.transition = 'none';
  };

  const onTouchMove = (e) => {
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;
    if (!decided.current) {
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      if (absDy > 6) { decided.current = true; swiping.current = false; return; }
      if (absDx > 12 && absDx > absDy * 2) { decided.current = true; swiping.current = true; }
      else if (absDx > 12) { decided.current = true; swiping.current = false; return; }
      else return;
    }
    if (!swiping.current) return;
    if (dx > 0) { currentX.current = 0; return; }
    currentX.current = dx;
    const el = wrapRef.current?.querySelector('.pv-list-item');
    if (el) el.style.transform = `translateX(${dx}px)`;
  };

  const onTouchEnd = () => {
    if (!swiping.current) return;
    const el = wrapRef.current?.querySelector('.pv-list-item');
    if (!el) return;
    el.style.transition = 'transform 0.25s ease-out';
    if (currentX.current < -100) {
      el.style.transform = 'translateX(-120%)';
      setTimeout(() => onDelete(ticker), 200);
    } else {
      el.style.transform = 'translateX(0)';
    }
  };

  return (
    <div className="pv-swipe-wrap" ref={wrapRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}>
      <div className="pv-swipe-bg">
        <span className="pv-swipe-label">Entfernen</span>
      </div>
      {children}
    </div>
  );
}

const STORAGE_KEY = 'finanzapp_portfolio_v1';

const SIGNAL_LABEL = { 'Kaufen': 'BUY', 'Verkaufen': 'SELL', 'Halten': 'HOLD' };
const SIGNAL_CLASS = { 'Kaufen': 'buy', 'Verkaufen': 'sell', 'Halten': 'hold' };

const DEFAULT_PORTFOLIO = [
  { ticker: 'AAPL', name: 'Apple Inc.', type: 'stock' },
  { ticker: 'NVDA', name: 'NVIDIA Corp.', type: 'stock' },
];

function loadPortfolio() {
  try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) : DEFAULT_PORTFOLIO; }
  catch { return DEFAULT_PORTFOLIO; }
}

function savePortfolio(list) {
  const json = JSON.stringify(list);
  localStorage.setItem(STORAGE_KEY, json);
  setItem(STORAGE_KEY, json);
}

function PortfolioView({ assets, addTicker, removeTicker, portfolioVersion, onRegisterDetailCloser, openTicker, onOpenTickerHandled, onShowPaywall, addTrigger }) {
  const [portfolio, setPortfolio]         = useState(loadPortfolio);
  const [showAdd, setShowAdd]             = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const { getLimit }                      = useFreemium();

  useEffect(() => {
    onRegisterDetailCloser?.(selectedAsset ? () => setSelectedAsset(null) : null);
  }, [selectedAsset, onRegisterDetailCloser]);

  useEffect(() => {
    if (!openTicker) return;
    const live = assets.find(a => a.ticker === openTicker);
    if (live) setSelectedAsset(live);
    onOpenTickerHandled?.();
  }, [openTicker]); // eslint-disable-line

  const { removeAlerts, onAssetsUpdate } = useSignalAlerts();

  useEffect(() => {
    portfolio.forEach(({ ticker, name, type }) => addTicker(ticker, name, type));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!portfolioVersion) return;
    const fresh = loadPortfolio();
    setPortfolio(fresh);
    fresh.forEach(({ ticker, name, type }) => addTicker(ticker, name, type));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioVersion]);

  useEffect(() => { onAssetsUpdate(assets); }, [assets, onAssetsUpdate]);

  // Lupe in der Header-Bar (von App.js): Counter erhoeht → Add-Dialog oeffnen.
  // Initial-Render (addTrigger=0) ueberspringen.
  useEffect(() => {
    if (!addTrigger) return;
    const limit = getLimit('portfolioAssets');
    if (limit != null && portfolio.length >= limit) {
      onShowPaywall?.();
      return;
    }
    setShowAdd(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addTrigger]);

  // No buyPrice backfill — GESAMT only shows when buyPriceManual is set

  const handleAdd = ({ ticker, name, type }) => {
    const entry = { ticker, name, type };
    const updated = [...portfolio, entry];
    setPortfolio(updated);
    savePortfolio(updated);
    addTicker(ticker, name, type);
  };

  const handleRemove = (eOrTicker, tickerArg) => {
    let ticker;
    if (typeof eOrTicker === 'string') {
      ticker = eOrTicker;
    } else {
      eOrTicker?.stopPropagation?.();
      ticker = tickerArg;
    }
    const updated = portfolio.filter(p => p.ticker !== ticker);
    setPortfolio(updated);
    savePortfolio(updated);
    removeTicker(ticker);
    removeAlerts(ticker);
  };

  const existingTickers = portfolio.map(p => p.ticker);
  const SIGNAL_ORDER = { 'Verkaufen': 0, 'Kaufen': 1, 'Halten': 2 };

  const portfolioWithData = portfolio
    .map(entry => ({
      ...entry,
      live: assets.find(a => a.ticker === entry.ticker),
    }))
    .sort((a, b) => {
      const sa = SIGNAL_ORDER[a.live?.signal] ?? 3;
      const sb = SIGNAL_ORDER[b.live?.signal] ?? 3;
      return sa - sb;
    });

  return (
    <div className="portfolio-view">
      {/* Header */}
      <div className="pv-header">
        <div className="pv-header-left">
          <span className="pv-header-label">Portfolio-Übersicht</span>
          <h2 className="pv-header-title">Watchlist</h2>
          <span className="pv-header-sub">{portfolio.length} aktive Position{portfolio.length !== 1 ? 'en' : ''}</span>
        </div>
      </div>

      {portfolioWithData.length === 0 && !showAdd ? (
        <p className="pv-empty">Noch keine Assets. Tippe auf + um zu beginnen.</p>
      ) : (
        <div className="pv-list">
          {portfolioWithData.map(({ ticker, name, buyPrice, buyPriceManual, live }) => {
            const price     = live?.price;
            const change24h = (price && price > 0) ? (live?.change ?? 0) : 0;
            const signal    = live?.signal;
            const signalClass = SIGNAL_CLASS[signal] || 'hold';
            const signalLabel = SIGNAL_LABEL[signal] || 'HOLD';
            const pnlPct    = buyPriceManual && buyPrice && price > 0
              ? ((price - buyPrice) / buyPrice) * 100
              : null;

            return (
              <SwipeToDelete key={ticker} ticker={ticker} onDelete={handleRemove}>
                <article
                  className="pv-list-item"
                  onClick={() => { if (live) setSelectedAsset(live); }}
                >
                  <TickerLogo ticker={ticker} />
                  <div className="pv-item-meta">
                    <span className="pv-item-ticker">{ticker}</span>
                    <span className="pv-item-name">{live?.name || name}</span>
                  </div>
                  <div className="pv-item-prices">
                    <span className="pv-item-price">{price > 0 ? fmtPrice(price) : '—'}</span>
                    <span className={`pv-item-change ${change24h >= 0 ? 'pos' : 'neg'}`}>
                      {price > 0 ? fmt(change24h) : '—'}
                    </span>
                    {pnlPct != null && (
                      <span className={`pv-item-gesamt ${pnlPct >= 0 ? 'pos' : 'neg'}`}>
                        {fmt(pnlPct)}
                      </span>
                    )}
                  </div>
                  <span className={`pv-signal-badge pv-signal-badge--sm ${signalClass}`}>
                    {signalLabel}
                  </span>
                </article>
              </SwipeToDelete>
            );
          })}
        </div>
      )}

      {showAdd && (
        <PortfolioAdd
          onAdd={handleAdd}
          onClose={() => setShowAdd(false)}
          existingTickers={existingTickers}
        />
      )}

      {selectedAsset && <AssetDetail asset={selectedAsset} onClose={() => setSelectedAsset(null)} />}

      <button className="pv-add-btn" onClick={() => {
        const limit = getLimit('portfolioAssets');
        if (limit != null && portfolio.length >= limit) {
          onShowPaywall?.();
        } else {
          setShowAdd(v => !v);
        }
      }}>
        + Asset hinzufügen
      </button>
    </div>
  );
}

export default PortfolioView;
